import { ChangeDetectionStrategy, Component, Injector } from "@angular/core";
import { LoggerService } from "@bfx/logger";
import { GridApi, RowNode } from "ag-grid-community";
import { TriangulationUtil } from "app/bfx-detail-fill/models/TriangulationUtil";
import {
  NUMBER_SPINNER_INPUTS,
  NUMBER_SPINNER_VIEW_PROVIDERS,
  ZERO,
} from "shared/base-components/number-spinner.component";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { CurrencyPairImpl } from "shared/cubiq-model-multi-asset";
import { CcyPairPromiseLookupService } from "shared/cubiq-services";
import { SPINNER_VALUE_ACCESSOR } from "../../bfx-interface-controls/bfx-price-spinner.component";
import { DetailFillSpinnerType } from "./DetailFillSpinnerType";
import { DfPriceSpinner } from "./DfPriceSpinner";
import { BfxFieldNumberDirective } from "../../../shared/base-components/bfx-field-number";
import { BfxInputDirective } from "../../../shared/base-components/bfx-input";

const logger = LoggerService.for(__dirname);

@Component({
  selector: "df-rate-spinner",
  templateUrl: "../../../shared/base-components/html/number-spinner.html",
  providers: [SPINNER_VALUE_ACCESSOR],
  viewProviders: NUMBER_SPINNER_VIEW_PROVIDERS,
  inputs: NUMBER_SPINNER_INPUTS,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DetailFillRateSpinner extends DfPriceSpinner {
  private currentData: any;
  private api: GridApi;
  protected type: DetailFillSpinnerType;

  protected rowNode: RowNode = null;

  constructor(
    injector: Injector,
    inputDirective: BfxInputDirective,
    fieldDirective: BfxFieldNumberDirective,
    currencyPairService: CcyPairPromiseLookupService,
  ) {
    super(injector, inputDirective, fieldDirective, currencyPairService);
    //        this.type = type;
  }

  agInit(params: any): void {
    super.agInit(params);
    this.currentData = params.node.data;
    this.api = params.api;
    if (this.isCall()) {
      this.currentData.markup =
        this.currentData.markup == null ? new CubiqTS.BigNumber(0) : this.currentData.markup;
    }
  }

  protected override getMinimum(): CubiqTS.BigNumber {
    if (
      this.type == DetailFillSpinnerType.CLIENT_PTS ||
      this.type == DetailFillSpinnerType.MARKET_PTS
    ) {
      return null;
    }
    return ZERO;
  }

  isCancelBeforeStart?(): boolean {
    return this.isComponentLeg || (!this.isComponentLeg && !this.isRowSelected);
  }

  isCancelAfterEnd?(): boolean {
    return this.isComponentLeg || (!this.isComponentLeg && !this.isRowSelected);
  }

  public onChange(): void {
    super.onChange();

    if (this.currentData) {
      let dirtyCols: string[] = [];
      switch (this.type) {
        case DetailFillSpinnerType.MARKET_SPOT: {
          this.updateMarketSpot(this.data.textValue, dirtyCols);
          break;
        }
        case DetailFillSpinnerType.MARKET_PTS: {
          this.updateMarketPts(this.data.textValue, dirtyCols);
          break;
        }
        case DetailFillSpinnerType.MARKET_ALL_IN: {
          this.updateMarketAllIn(this.data.textValue, dirtyCols);
          break;
        }
        case DetailFillSpinnerType.CLIENT_SPOT: {
          this.updateClientSpot(this.data.textValue, dirtyCols);
          if (this.isCall()) {
            this.updateMarketSpot(this.data.textValue, dirtyCols);
          }
          break;
        }
        case DetailFillSpinnerType.CLIENT_PTS: {
          this.updateClientPts(this.data.textValue, dirtyCols, true);
          break;
        }
        case DetailFillSpinnerType.CLIENT_ALL_IN: {
          this.updateClientAllIn(this.data.textValue, dirtyCols, true);
          break;
        }
      }

      if (this.api && Array.isArray(dirtyCols) && dirtyCols.length > 0) {
        if (this.currentData.vwapUpdateSubject != null) {
          this.currentData.vwapUpdateSubject.next();
        }

        this.api.refreshCells({ columns: dirtyCols });
      }
    }
  }

  private updateMarketSpot(val: string, dirtyCols: string[]) {
    let newMarketSpot: CubiqTS.BigNumber = new CubiqTS.BigNumber(val);

    // Don't recalculate if the new market spot is not a number
    if (newMarketSpot.isNaN()) {
      return;
    }

    this.currentData.spotMarketRate = newMarketSpot.toFixed(this.rateDigits);

    if (this.currentData.isComponentLeg) {
      if (this.currentData.leg1) {
        // we are triangulate leg1 after change to this.currentData (leg1)
        TriangulationUtil.triangulateLegs(
          this.currentData,
          this.currentData.leg1,
          this.currentData.parentOrder,
        );
        this.currentData.leg1.lastModified = false;
      } else {
        // we are triangulate leg2 after change to this.currentData (leg1)
        TriangulationUtil.triangulateLegs(
          this.currentData,
          this.currentData.leg2,
          this.currentData.parentOrder,
        );
        this.currentData.leg2.lastModified = false;
      }
      this.currentData.lastModified = true;
    } else {
      // we are editing the prime
      if (this.currentData.leg1 && this.currentData.leg2) {
        TriangulationUtil.triangulateFromOrder(
          this.currentData,
          this.currentData.leg1,
          this.currentData.leg2,
        );
      }

      // if (!this.isTP()) {
      let currMarkup: CubiqTS.BigNumber = new CubiqTS.BigNumber(this.currentData.markup);
      let newClientSpot;

      if (!this.clientBuysBase()) {
        newClientSpot = newMarketSpot.minus(currMarkup);
      } else {
        newClientSpot = newMarketSpot.plus(currMarkup);
      }
      this.updateClientSpot(newClientSpot.toFixed(this.rateDigits), dirtyCols);
      // }

      this.updateMarketAllIn(null, dirtyCols);
    }
    dirtyCols.push("spotMarketRate");
  }

  private updateMarketPts(val: string, dirtyCols: string[]) {
    if (null != val) {
      // its in pips - convert to poinst and store
      this.currentData.forwardMarketPoints = val;

      // if (!this.isTP()) {
      this.updateClientPts(val, dirtyCols, false);
      // }

      this.updateMarketAllIn(null, dirtyCols);
    } else {
      this.currentData.forwardMarketPoints = this.convertRateToPoints(
        new CubiqTS.BigNumber(this.currentData.forwardMarketRate).minus(
          this.currentData.spotMarketRate,
        ),
      );

      // if (!this.isTP()) {
      this.updateClientPts(this.currentData.forwardMarketPoints, dirtyCols, false);
      // }
    }
    dirtyCols.push("forwardMarketPoints");
  }

  private updateMarketAllIn(val: string, dirtyCols: string[]) {
    if (null != val) {
      this.currentData.forwardMarketRate = val;

      this.updateMarketPts(null, dirtyCols);
    } else {
      let points: string = this.convertPipsToPoints(this.currentData.forwardMarketPoints);
      let adjustment: CubiqTS.BigNumber = new CubiqTS.BigNumber(points);
      let mktAllIn: string = new CubiqTS.BigNumber(this.currentData.spotMarketRate)
        .plus(adjustment)
        .toFixed(this.rateDigits);
      this.currentData.forwardMarketRate = mktAllIn;
    }
    dirtyCols.push("forwardMarketRate");

    // if (!this.isTP()) {
    this.updateClientAllIn(null, dirtyCols, false);
    // }
    if (!this.isCall()) {
      this.updateMarkup(dirtyCols);
    }
  }

  private updateClientSpot(val: string, dirtyCols: string[]) {
    if (null != val) {
      this.currentData.clientSpot = val;
    } else {
      if (this.clientBuysBase()) {
        this.currentData.clientSpot = new CubiqTS.BigNumber(this.currentData.spotMarketRate)
          .plus(this.currentData.markup)
          .toFixed(this.rateDigits);
      } else {
        this.currentData.clientSpot = new CubiqTS.BigNumber(this.currentData.spotMarketRate)
          .minus(this.currentData.markup)
          .toFixed(this.rateDigits);
      }
    }

    dirtyCols.push("clientSpot");

    this.updateClientAllIn(null, dirtyCols, false);
    if (!this.isCall()) {
      this.updateMarkup(dirtyCols);
    }
  }

  private updateClientPts(
    val: string,
    dirtyCols: string[],
    clientRatesUpdateExecutionFlow: boolean,
  ) {
    if (null != val) {
      this.currentData.forwardPoints = val;
      if (this.isCall() && clientRatesUpdateExecutionFlow) {
        this.updateMarketPts(val, dirtyCols);
      }
    } else {
      this.currentData.forwardPoints = this.convertRateToPoints(
        new CubiqTS.BigNumber(this.currentData.allInClientRate).minus(this.currentData.clientSpot),
      );
      if (this.isCall() && clientRatesUpdateExecutionFlow) {
        this.updateMarketPts(this.currentData.forwardPoints, dirtyCols);
      }
    }
    dirtyCols.push("forwardPoints");

    this.updateClientAllIn(null, dirtyCols, clientRatesUpdateExecutionFlow);
    if (!this.isCall()) {
      this.updateMarkup(dirtyCols);
    }
  }

  private updateClientAllIn(
    val: string,
    dirtyCols: string[],
    clientRatesUpdateExecutionFlow: boolean,
  ) {
    if (null != val) {
      this.currentData.allInClientRate = val;
      this.updateClientPts(null, dirtyCols, clientRatesUpdateExecutionFlow);

      // only updating markup when val entered - if pushed from other value - I leave it to them - to prevent cycles.
      if (!this.isCall()) {
        this.updateMarkup(dirtyCols);
      }
    } else {
      let points: string = this.convertPipsToPoints(this.currentData.forwardPoints);
      let adjustment = new CubiqTS.BigNumber(points);
      let clientAllIn: string = new CubiqTS.BigNumber(this.currentData.clientSpot)
        .plus(adjustment)
        .toFixed(this.rateDigits);
      this.currentData.allInClientRate = clientAllIn;
    }
    dirtyCols.push("allInClientRate");

    if (this.isCall() && clientRatesUpdateExecutionFlow) {
      this.updateMarketAllIn(null, dirtyCols);
    }
  }

  private updateMarkup(dirtyCols: string[]) {
    let markup: string;

    if (this.clientBuysBase()) {
      markup = new CubiqTS.BigNumber(this.currentData.allInClientRate)
        .minus(this.currentData.forwardMarketRate)
        .toFixed(this.rateDigits);
    } else {
      markup = new CubiqTS.BigNumber(this.currentData.forwardMarketRate)
        .minus(this.currentData.allInClientRate)
        .toFixed(this.rateDigits);
    }

    this.currentData.markup = markup;
    dirtyCols.push("markup");
  }

  private isCall(): boolean {
    return this.currentData.orderType == CubiqTS.OrderType.LIMIT_CALL;
  }

  private isTP(): boolean {
    return this.currentData.orderType == CubiqTS.OrderType.LIMIT_TP;
  }

  private clientBuysBase(): boolean {
    return this.currentData.clientBuysBase;
  }

  protected getRateDigits(pair: CurrencyPairImpl): number {
    if (this.rateDigits) {
      return this.rateDigits;
    }
    this.calcPairConstants(pair);

    return this.rateDigits;
  }

  protected getRateStep(pair: CurrencyPairImpl): CubiqTS.BigNumber {
    if (!this.rateStep) {
      let step = this.getStep(pair);
      this.rateStep = step;
    }
    return this.rateStep;
  }

  protected getPointsDigits(pair: CurrencyPairImpl): number {
    if (this.pointsDigits) {
      return this.pointsDigits;
    }
    this.calcPairConstants(pair);

    return this.pointsDigits;
  }

  protected getPointsStep(pair: CurrencyPairImpl): CubiqTS.BigNumber {
    if (!this.pointsStep) {
      let ret: CubiqTS.BigNumber = new CubiqTS.BigNumber(1);
      if (pair != null) {
        let pointsPrecision = this.getPointsDigits(pair);
        ret = ret.times(new CubiqTS.BigNumber("10").pow(-pointsPrecision));
      }
      this.pointsStep = ret;
    }
    return this.pointsStep;
    //        return this.pipsToPoints;
  }

  private convertPipsToPoints(val: string): string {
    let ret: string = new CubiqTS.BigNumber(val).times(this.pipsToPoints).toFixed(this.rateDigits);
    return ret;
  }

  private convertPointsToPips(val: CubiqTS.BigNumber): string {
    let ret: string = val.times(this.pointsToPips).toFixed(this.rateDigits);
    return ret;
  }

  private convertRateToPoints(val: CubiqTS.BigNumber): string {
    let ret: string = val.times(this.pointsToPips).toFixed(this.pointsDigits);
    return ret;
  }
}
